import React, { useEffect } from 'react';
import ReactSelect from 'react-select';

import { getContacts } from '../../api/contacts';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getUserFullName, classNames } from '../helpers/functions';

import { AvatarGenerated } from '../../components/Avatar';

const sortObject = (o) =>
	Object.keys(o)
		.sort()
		.reduce((r, k) => ((r[k] = o[k]), r), {});

const CustomOption = ({ innerProps, isDisabled, ...rest }) => {
	return !isDisabled ? (
		<div {...innerProps} className={'cursor-pointer hover:bg-gray-100'}>
			<div className="flex items-center py-3 px-5">
				<AvatarGenerated
					type={rest.data.type}
					id={
						rest.data.type === 'person'
							? getUserFullName(rest.data.contact)
							: rest.data.value
					}
					size={'xs'}
				/>
				<span className="ml-3 text-sm">{rest.data.label}</span>
			</div>
		</div>
	) : null;
};

export default ({
	key,
	placeholder,
	contacts,
	allSelected,
	value,
	onChange,
	onClear,
	skip = [],
	isClearable,
	Option,
	className,
}) => {
	const { open, setOpen, setClose } = useModal('selectContacts');

	const { value: localContacts, execute: onFetchContacts } = useAsync(
		getContacts,
		{ immediate: false }
	);

	useEffect(() => {
		if (typeof contacts === 'undefined') {
			onFetchContacts();
		}
	}, []);

	const finalContacts = localContacts || contacts;

	const sorted =
		finalContacts?.rows?.reduce((groups, item) => {
			const directoryLetter = (
				item?.lastName ||
				item?.firstName ||
				item?.companyName
			)
				?.substring(0, 1)
				.toUpperCase();

			return {
				...groups,
				[directoryLetter]: [...(groups[directoryLetter] || []), item],
			};
		}, {}) || [];

	const label = (contact) => ({
		label:
			contact?.type === 'company'
				? contact?.companyName
				: getUserFullName(contact),
		value: contact.id,
		type: contact.type,
		contact,
		disabled: skip?.includes(contact.id),
	});

	const options = Object.keys(sortObject(sorted)).map((key) => ({
		label: key === 'undefined' ? '' : key,
		options: sorted[key]?.map(label),
	}));

	const selectedContact = value
		? finalContacts?.rows?.find((o) => o.id === value)
		: null;
	const selectedOption = selectedContact ? label(selectedContact) : null;

	return (
		<ReactSelect
			className={classNames('text-sm', className)}
			classNames={{
				control: (state) => 'rounded-md',
			}}
			options={options}
			onChange={(change) =>
				onChange(
					change
						? finalContacts?.rows?.find((o) => o.id === change.value)
						: null
				)
			}
			placeholder={placeholder || ''}
			value={selectedOption}
			isOptionDisabled={(option) => option.disabled}
			isClearable={isClearable}
			components={{ Option: Option || CustomOption }}
		/>
	);
};
